<script setup lang="ts">
import { computed } from "vue";

import type { ProjectHazard } from "@/api/client";

// Surfaces the project's site hazards (from Settings) on RAMS/briefing forms:
// a caution banner + one-click chips to pull each into the hazards list. In
// `readonly` mode (e.g. a check sheet, which has no hazards list) it's a plain
// caution — no add actions.
const props = defineProps<{ hazards: ProjectHazard[]; readonly?: boolean }>();
const emit = defineEmits<{ add: [hazard: ProjectHazard]; addAll: [] }>();

const active = computed(() => props.hazards.filter((h) => !h.archived_at));
</script>

<template>
  <div
    v-if="active.length"
    class="rounded-lg border border-semantic-warning/40 bg-semantic-warning/5 p-3 text-sm"
  >
    <div class="mb-1 flex items-center justify-between gap-2">
      <span class="font-medium text-neutral-900">
        ⚠ {{ active.length }} site hazard(s) on this project
      </span>
      <button
        v-if="!readonly"
        type="button"
        class="shrink-0 rounded-md border border-semantic-warning/50 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-semantic-warning/10"
        @click="emit('addAll')"
      >
        Add all to hazards
      </button>
    </div>
    <p class="mb-2 text-xs text-neutral-500">
      From the site hazard register —
      {{ readonly ? "keep these in mind during the inspection." : "make sure the controls and permits below cover these." }}
    </p>
    <div class="flex flex-wrap gap-1.5">
      <!-- readonly: plain tags; editable: click to add to the hazards list -->
      <span
        v-for="h in active"
        :key="h.hazard_id"
        class="rounded-full border border-neutral-300 px-2.5 py-0.5 text-xs"
        :class="readonly ? 'bg-neutral-50 text-neutral-600' : 'cursor-pointer bg-neutral-0 hover:bg-neutral-50'"
        :title="h.description"
        @click="!readonly && emit('add', h)"
      >
        <template v-if="!readonly">+ </template>{{ h.category }}<span
          v-if="h.location"
          class="text-neutral-400"
        > · {{ h.location }}</span>
      </span>
    </div>
  </div>
</template>
